import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { PLATFORM_LOG_KINDS, PLATFORM_LOG_LABELS, STAFF_ROLES } from '@app/shared';
import type {
  CompanyDto,
  HqAnalyticsDto,
  HqCompanyDto,
  HqFigures,
  PlatformAuthStateDto,
  PlatformLogPage,
  StaffCapability,
  StaffRole,
  SuperAdminDto,
} from '@app/shared';
import { AppLockup } from '../../components/BrandLogo';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { useNotify } from '../../components/NotifyProvider';
import { api } from '../../lib/api';
import { formatCents } from '../../lib/format';
import { useFeatureReleased } from '../../release';

/**
 * The super instance (hq.212ez.com, switch `hq-instance`), owner 2026-11-04/05: 212Ez's staff sign in,
 * see every company's figures summed (or one company's), open any company's full app as 212Ez support
 * (or 212Ez's own sandbox), manage companies, read every log and manage 212Ez's staff. Each tab shows
 * only for the levels that may use it (STAFF_CAN).
 */
const STATE = ['hq', 'state'];
/** A row of fields: each its own width from a phone up (the theme makes fields full width). */
const FIELD_ROW = {
  display: 'flex',
  gap: 1.5,
  flexWrap: 'wrap',
  alignItems: 'center',
  '& .MuiFormControl-root': { width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 200 } },
} as const;
const errorText = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong');

export function HqPage() {
  const queryClient = useQueryClient();
  const state = useQuery({
    queryKey: STATE,
    queryFn: () => api.get<PlatformAuthStateDto>('/platform/auth/state'),
  });
  const me = state.data?.signedIn;
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0f1f5c', p: { xs: 1, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <AppLockup height={56} />
          <Typography variant="h5" component="h1" sx={{ flex: 1 }}>
            212Ez HQ
          </Typography>
          {me ? (
            <>
              <Typography variant="body2">
                {me.name} · <b>{me.role}</b>
              </Typography>
              <Button
                size="small"
                onClick={() =>
                  void api
                    .post('/platform/auth/logout')
                    .then(() => queryClient.invalidateQueries({ queryKey: STATE }))
                }
              >
                Sign out
              </Button>
            </>
          ) : null}
        </Box>
        {state.isPending ? null : me ? (
          <SignedIn can={me.can} loginId={me.loginId} />
        ) : (
          <SignIn onDone={() => void queryClient.invalidateQueries({ queryKey: STATE })} />
        )}
      </Paper>
    </Box>
  );
}

function SignIn({ onDone }: { onDone: () => void }) {
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await api.post('/platform/auth/login', { loginId, password });
      onDone();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box
      component="form"
      onSubmit={(e) => void submit(e)}
      sx={{ display: 'grid', gap: 2, maxWidth: 420, mx: 'auto' }}
    >
      <Typography variant="body2" color="text.secondary">
        For 212Ez's own team. Sign in with your 212Ez login.
      </Typography>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <TextField
        label="Login ID"
        value={loginId}
        onChange={(e) => setLoginId(e.target.value)}
        autoComplete="username"
        required
      />
      <TextField
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="current-password"
        required
      />
      <Button type="submit" variant="contained" disabled={busy}>
        Sign in
      </Button>
    </Box>
  );
}

type TabKey = 'analytics' | 'companies' | 'logs' | 'staff';

function SignedIn({ can, loginId }: { can: StaffCapability[]; loginId: string }) {
  const featureOn = useFeatureReleased();
  const has = (what: StaffCapability) => can.includes(what);
  const tabs: Array<[TabKey, string]> = [
    ...(has('analytics') && featureOn('hq-analytics')
      ? [['analytics', 'Analytics'] as [TabKey, string]]
      : []),
    ['companies', 'Companies'],
    ...(has('logs') && featureOn('platform-company-records')
      ? [['logs', 'Logs'] as [TabKey, string]]
      : []),
    ...(has('staff') ? [['staff', '212Ez staff'] as [TabKey, string]] : []),
  ];
  const [tab, setTab] = useState<TabKey>(tabs[0]![0]);
  return (
    <>
      <Tabs value={tab} onChange={(_e, v: TabKey) => setTab(v)} sx={{ mb: 2 }}>
        {tabs.map(([key, label]) => (
          <Tab key={key} value={key} label={label} />
        ))}
      </Tabs>
      {tab === 'analytics' ? <Analytics canManage={has('companies.manage')} /> : null}
      {tab === 'companies' ? <Companies has={has} /> : null}
      {tab === 'logs' ? <Logs /> : null}
      {tab === 'staff' ? <Staff me={loginId} /> : null}
    </>
  );
}

/* --------------------------------- Analytics -------------------------------- */

const FIGURES: Array<[keyof HqFigures, string, boolean]> = [
  ['lines', 'Lines', false],
  ['prepaid', 'Prepaid', false],
  ['postpaid', 'Postpaid', false],
  ['lifeline', 'Lifeline', false],
  ['charity', 'Charity', false],
  ['activations', 'Activations', false],
  ['disconnects', 'Disconnects', false],
  ['portIns', 'Port-ins', false],
  ['portOuts', 'Port-outs', false],
  ['revenueCents', 'Revenue', true],
  ['taxCents', 'Tax', true],
  ['refundsCents', 'Refunds', true],
  ['netRevenueCents', 'Net revenue', true],
  ['wholesaleCostCents', 'Wholesale cost', true],
  ['profitCents', 'Est. profit', true],
];
const show = (f: HqFigures, key: keyof HqFigures, money: boolean) =>
  money ? formatCents(f[key]) : String(f[key]);

function Analytics({ canManage }: { canManage: boolean }) {
  const notify = useNotify();
  const companies = useQuery({
    queryKey: ['hq', 'companies'],
    queryFn: () => api.get<{ data: HqCompanyDto[] }>('/platform/hq/companies').then((r) => r.data),
  });
  const [draft, setDraft] = useState({ companyId: '', from: '', to: '' });
  const [applied, setApplied] = useState(draft);
  const data = useQuery({
    queryKey: ['hq', 'analytics', applied],
    queryFn: () => api.get<HqAnalyticsDto>('/platform/hq/analytics', applied),
  });
  const [cost, setCost] = useState<Record<string, string>>({});
  const saveCost = async (id: string) => {
    const dollars = Number(cost[id]);
    if (!Number.isFinite(dollars) || dollars < 0) return notify.error('Enter the cost in dollars');
    try {
      await api.put(`/platform/hq/carriers/${id}/cost`, {
        costPerLineCents: Math.round(dollars * 100),
      });
      notify.success('Wholesale cost saved.');
      void data.refetch();
    } catch (err) {
      notify.error(err);
    }
  };
  const d = data.data;
  const companyColumns: Column<HqAnalyticsDto['companies'][number]>[] = [
    { key: 'name', header: 'Company', render: (c) => c.name },
    {
      key: 'stage',
      header: 'Stage',
      render: (c) => (c.status === 'Inactive' ? 'Suspended' : c.stage),
    },
    ...FIGURES.map(([key, header, money]) => ({
      key,
      header,
      align: 'right' as const,
      render: (c: HqAnalyticsDto['companies'][number]) => show(c, key, money),
    })),
  ];
  const carrierColumns: Column<HqAnalyticsDto['carriers'][number]>[] = [
    { key: 'company', header: 'Company', render: (c) => c.company },
    { key: 'code', header: 'Carrier', render: (c) => `${c.code} (${c.name})` },
    { key: 'lines', header: 'Lines', align: 'right', render: (c) => String(c.lines) },
    { key: 'share', header: 'Share', align: 'right', render: (c) => `${c.sharePercent}%` },
    {
      key: 'cost',
      header: 'Cost per line a month',
      render: (c) =>
        canManage ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              label="$"
              value={cost[c.id] ?? (c.costPerLineCents / 100).toFixed(2)}
              onChange={(e) => setCost((x) => ({ ...x, [c.id]: e.target.value }))}
              sx={{ width: 110 }}
              slotProps={{
                htmlInput: {
                  inputMode: 'decimal',
                  'aria-label': `Cost per line for ${c.code} of ${c.company}`,
                },
              }}
            />
            <Button size="small" onClick={() => void saveCost(c.id)}>
              Save
            </Button>
          </Box>
        ) : (
          formatCents(c.costPerLineCents)
        ),
    },
    {
      key: 'monthly',
      header: 'Wholesale a month',
      align: 'right',
      render: (c) => formatCents(c.monthlyCostCents),
    },
  ];
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          setApplied(draft);
        }}
        sx={FIELD_ROW}
      >
        <TextField
          select
          label="Company"
          value={draft.companyId}
          onChange={(e) => setDraft((x) => ({ ...x, companyId: e.target.value }))}
          sx={{ minWidth: 240 }}
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
        >
          <MenuItem value="">All companies</MenuItem>
          {(companies.data ?? []).map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.sandbox ? '212Ez (sandbox)' : c.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          type="date"
          label="From"
          value={draft.from}
          onChange={(e) => setDraft((x) => ({ ...x, from: e.target.value }))}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          type="date"
          label="To"
          value={draft.to}
          onChange={(e) => setDraft((x) => ({ ...x, to: e.target.value }))}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <Button type="submit" variant="contained">
          Show
        </Button>
        <Typography variant="caption" color="text.secondary">
          Lines are counted now; the rest over the days chosen (the last 30 by default).
        </Typography>
      </Box>
      {data.isError ? <Alert severity="error">{errorText(data.error)}</Alert> : null}
      {d ? (
        <>
          <Box
            aria-label="Totals"
            sx={{
              display: 'grid',
              gap: 1,
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            }}
          >
            {FIGURES.map(([key, label, money]) => (
              <Paper key={key} variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="h6">{show(d.totals, key, money)}</Typography>
              </Paper>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {applied.companyId
              ? 'One company.'
              : "Every company summed; 212Ez's sandbox is left out."}{' '}
            Revenue is money taken (wallet-paid invoices left out, as that money was counted when it
            went into the wallet). Profit is net revenue minus each carrier's wholesale cost per
            line, an estimate. Carrier usage needs the aggregators' usage feeds.
          </Typography>
          <Typography variant="h6">Per company</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <DataTable
              label="Per company"
              columns={companyColumns}
              rows={d.companies}
              rowKey={(c) => c.id}
            />
          </Box>
          <Typography variant="h6">Per carrier</Typography>
          <DataTable
            label="Per carrier"
            columns={carrierColumns}
            rows={d.carriers}
            rowKey={(c) => c.id}
            emptyText="No carrier yet."
          />
        </>
      ) : null}
    </Box>
  );
}

/* --------------------------------- Companies -------------------------------- */

function Companies({ has }: { has: (what: StaffCapability) => boolean }) {
  const notify = useNotify();
  const featureOn = useFeatureReleased();
  const list = useQuery({
    queryKey: ['hq', 'company-list'],
    queryFn: () => api.get<{ data: CompanyDto[] }>('/platform/companies').then((r) => r.data),
  });
  const sandbox = useQuery({
    queryKey: ['hq', 'companies'],
    queryFn: () => api.get<{ data: HqCompanyDto[] }>('/platform/hq/companies').then((r) => r.data),
  });
  const [shown, setShown] = useState('');
  const sandboxIds = new Set((sandbox.data ?? []).filter((c) => c.sandbox).map((c) => c.id));

  const open = async (path: string) => {
    try {
      await api.post(path);
      // A new app session for that company: start the app afresh in it.
      window.location.assign('/');
    } catch (err) {
      notify.error(err);
    }
  };
  const change = async (c: CompanyDto, body: object, question: string) => {
    if (!window.confirm(question)) return;
    try {
      await api.put(`/platform/companies/${c.id}`, body);
      notify.success(`${c.name} saved.`);
      void list.refetch();
    } catch (err) {
      notify.error(err);
    }
  };
  const tempPassword = async (c: CompanyDto, adminId: string, loginId: string) => {
    if (!window.confirm(`Give ${loginId} of ${c.name} a temporary password? It signs them out.`))
      return;
    try {
      const r = await api.post<{ temporaryPassword: string }>(
        `/platform/companies/${c.id}/admins/${adminId}/temporary-password`,
      );
      setShown(`Temporary password for ${loginId}: ${r.temporaryPassword} (shown once).`);
    } catch (err) {
      notify.error(err);
    }
  };

  const columns: Column<CompanyDto>[] = [
    {
      key: 'name',
      header: 'Company',
      render: (c) => (sandboxIds.has(c.id) ? '212Ez (sandbox)' : c.name),
    },
    {
      key: 'stage',
      header: 'Stage',
      render: (c) => (
        <Box sx={{ display: 'grid', gap: 0.5 }}>
          <Chip
            size="small"
            label={c.status === 'Inactive' ? 'Suspended' : c.stage}
            color={c.stage === 'Live' ? 'success' : 'warning'}
          />
          {c.liveRequest ? (
            <Typography variant="caption">
              Live requested {c.liveRequest.at.slice(0, 10)}
            </Typography>
          ) : null}
        </Box>
      ),
    },
    { key: 'customers', header: 'Customers', align: 'right', render: (c) => String(c.customers) },
    { key: 'users', header: 'Users', align: 'right', render: (c) => String(c.users) },
    {
      key: 'carriers',
      header: 'Carriers connected',
      align: 'right',
      render: (c) => String(c.carriersConnected),
    },
    {
      key: 'actions',
      header: '',
      render: (c) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {has('companies.open') &&
          (sandboxIds.has(c.id) || featureOn('platform-support-session')) ? (
            <Button
              size="small"
              variant="contained"
              onClick={() =>
                void open(
                  sandboxIds.has(c.id)
                    ? '/platform/hq/sandbox-session'
                    : `/platform/companies/${c.id}/support-session`,
                )
              }
            >
              Open
            </Button>
          ) : null}
          {has('companies.manage') ? (
            <>
              <Button
                size="small"
                onClick={() =>
                  void change(
                    c,
                    { stage: c.stage === 'Trial' ? 'Live' : 'Trial' },
                    c.stage === 'Trial'
                      ? `Move ${c.name} to Live? Services it has not connected are then refused, never simulated.`
                      : `Move ${c.name} back to Trial? Services it has not connected are simulated again.`,
                  )
                }
              >
                {c.stage === 'Trial' ? 'Move to Live' : 'Back to Trial'}
              </Button>
              <Button
                size="small"
                color={c.status === 'Active' ? 'error' : 'primary'}
                onClick={() =>
                  void change(
                    c,
                    { status: c.status === 'Active' ? 'Inactive' : 'Active' },
                    c.status === 'Active'
                      ? `Suspend ${c.name}? Its users are signed out and cannot sign in.`
                      : `Reactivate ${c.name}?`,
                  )
                }
              >
                {c.status === 'Active' ? 'Suspend' : 'Reactivate'}
              </Button>
              {c.admins.map((a) => (
                <Button
                  key={a.id}
                  size="small"
                  onClick={() => void tempPassword(c, a.id, a.loginId)}
                >
                  Temporary password: {a.loginId}
                </Button>
              ))}
            </>
          ) : null}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {has('companies.open') ? (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={() => void open('/platform/hq/sandbox-session')}>
            Open 212Ez (sandbox)
          </Button>
          <Typography variant="body2" color="text.secondary">
            212Ez's own company, to test and rehearse every module. Open any company below to work
            in its full app; everything you change is recorded under your login.
          </Typography>
        </Box>
      ) : null}
      {shown ? (
        <Alert severity="success" onClose={() => setShown('')}>
          {shown}
        </Alert>
      ) : null}
      {has('companies.create') && featureOn('platform-new-company') ? (
        <NewCompany
          onDone={(text) => {
            setShown(text);
            void list.refetch();
          }}
        />
      ) : null}
      <Box sx={{ overflowX: 'auto' }}>
        <DataTable
          label="Companies"
          columns={columns}
          rows={list.data}
          rowKey={(c) => c.id}
          loading={list.isFetching}
          error={list.error}
          onRetry={() => void list.refetch()}
        />
      </Box>
    </Box>
  );
}

const BLANK_COMPANY = {
  companyName: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  loginId: '',
  carriers: '',
};

function NewCompany({ onDone }: { onDone: (text: string) => void }) {
  const notify = useNotify();
  const [v, setV] = useState(BLANK_COMPANY);
  const [busy, setBusy] = useState(false);
  const field = (key: keyof typeof BLANK_COMPANY, label: string) => (
    <TextField
      size="small"
      label={label}
      value={v[key]}
      onChange={(e) => setV((x) => ({ ...x, [key]: e.target.value }))}
      required={key !== 'carriers'}
    />
  );
  return (
    <Paper
      variant="outlined"
      component="form"
      aria-label="New company"
      sx={{ p: 2 }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        setBusy(true);
        api
          .post<{ companyName: string; loginId: string; temporaryPassword: string }>(
            '/platform/companies',
            v,
          )
          .then((r) => {
            setV(BLANK_COMPANY);
            onDone(
              `${r.companyName} is made, in Trial. Login ID ${r.loginId}, temporary password ${r.temporaryPassword} (shown once; give it to them privately).`,
            );
          })
          .catch((err) => notify.error(err))
          .finally(() => setBusy(false));
      }}
    >
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        New company
      </Typography>
      <Box sx={FIELD_ROW}>
        {field('companyName', 'Company name')}
        {field('firstName', 'First Name')}
        {field('lastName', 'Last Name')}
        {field('email', 'Email Address')}
        {field('phone', 'Phone Number')}
        {field('loginId', 'Login ID')}
        {field('carriers', 'Carriers')}
        <Button type="submit" variant="contained" disabled={busy}>
          Create company
        </Button>
      </Box>
    </Paper>
  );
}

/* ----------------------------------- Logs ----------------------------------- */

function Logs() {
  const [draft, setDraft] = useState({ kind: 'api', status: '', q: '' });
  const [applied, setApplied] = useState({ ...draft, page: 1 });
  const logs = useQuery({
    queryKey: ['hq', 'logs', applied],
    queryFn: () => api.get<PlatformLogPage>('/platform/logs', { ...applied, pageSize: 25 }),
  });
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          setApplied({ ...draft, page: 1 });
        }}
        sx={FIELD_ROW}
      >
        <TextField
          select
          label="Log"
          value={draft.kind}
          onChange={(e) => setDraft((x) => ({ ...x, kind: e.target.value }))}
          sx={{ minWidth: 280 }}
        >
          {PLATFORM_LOG_KINDS.map((k) => (
            <MenuItem key={k} value={k}>
              {PLATFORM_LOG_LABELS[k]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Result"
          value={draft.status}
          onChange={(e) => setDraft((x) => ({ ...x, status: e.target.value }))}
          sx={{ minWidth: 140 }}
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
        >
          <MenuItem value="">Any</MenuItem>
          <MenuItem value="success">Success</MenuItem>
          <MenuItem value="failure">Failure</MenuItem>
        </TextField>
        <TextField
          label="Search"
          value={draft.q}
          onChange={(e) => setDraft((x) => ({ ...x, q: e.target.value }))}
        />
        <Button type="submit" variant="contained">
          Search
        </Button>
      </Box>
      <DataTable
        label="Logs"
        columns={[
          { key: 'at', header: 'When', render: (r) => r.at.slice(0, 16).replace('T', ' ') },
          { key: 'company', header: 'Company', render: (r) => r.company },
          { key: 'what', header: 'What', render: (r) => r.what },
          { key: 'who', header: 'Who', render: (r) => r.who },
          { key: 'result', header: 'Result', render: (r) => r.result },
          {
            key: 'detail',
            header: 'Detail',
            render: (r) => (
              <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                {r.detail}
              </Typography>
            ),
          },
        ]}
        rows={logs.data?.data}
        rowKey={(r) => r.id}
        loading={logs.isFetching}
        error={logs.error}
        onRetry={() => void logs.refetch()}
        page={applied.page}
        pageSize={25}
        total={logs.data?.total ?? 0}
        onPageChange={(page) => setApplied((x) => ({ ...x, page }))}
      />
    </Box>
  );
}

/* ----------------------------------- Staff ---------------------------------- */

const BLANK_STAFF = { loginId: '', name: '', email: '', password: '', role: 'Sales' as StaffRole };

function Staff({ me }: { me: string }) {
  const notify = useNotify();
  const list = useQuery({
    queryKey: ['hq', 'staff'],
    queryFn: () => api.get<{ data: SuperAdminDto[] }>('/platform/super-admins').then((r) => r.data),
  });
  const [v, setV] = useState(BLANK_STAFF);
  const add = (e: FormEvent) => {
    e.preventDefault();
    api
      .post('/platform/super-admins', v)
      .then(() => {
        setV(BLANK_STAFF);
        notify.success(`${v.loginId} added as ${v.role}.`);
        void list.refetch();
      })
      .catch((err) => notify.error(err));
  };
  const toggle = (a: SuperAdminDto) =>
    api
      .put(`/platform/super-admins/${a.id}`, {
        status: a.status === 'Active' ? 'Inactive' : 'Active',
      })
      .then(() => list.refetch())
      .catch((err) => notify.error(err));
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        212Ez's own team, above every company's chain: Super user, Engineer, Customer Associate,
        Sales. Each level also has everything the levels below it have.
      </Typography>
      <DataTable
        label="212Ez staff"
        columns={[
          { key: 'loginId', header: 'Login ID', render: (a) => a.loginId },
          { key: 'name', header: 'Name', render: (a) => a.name },
          { key: 'role', header: 'Level', render: (a) => a.role },
          { key: 'status', header: 'Status', render: (a) => a.status },
          {
            key: 'last',
            header: 'Last sign-in',
            render: (a) => (a.lastLoginAt ?? '').slice(0, 10),
          },
          {
            key: 'act',
            header: '',
            // Your own login cannot be switched off (the server refuses it).
            render: (a) =>
              a.loginId === me ? null : (
                <Button
                  size="small"
                  color={a.status === 'Active' ? 'error' : 'primary'}
                  onClick={() => void toggle(a)}
                >
                  {a.status === 'Active' ? 'Switch off' : 'Switch on'}
                </Button>
              ),
          },
        ]}
        rows={list.data}
        rowKey={(a) => a.id}
        loading={list.isFetching}
      />
      <Paper
        variant="outlined"
        component="form"
        aria-label="Add to 212Ez staff"
        onSubmit={add}
        sx={{ p: 2, ...FIELD_ROW }}
      >
        <TextField
          size="small"
          label="Login ID"
          value={v.loginId}
          onChange={(e) => setV((x) => ({ ...x, loginId: e.target.value }))}
          required
        />
        <TextField
          size="small"
          label="Name"
          value={v.name}
          onChange={(e) => setV((x) => ({ ...x, name: e.target.value }))}
          required
        />
        <TextField
          size="small"
          label="Email Address"
          value={v.email}
          onChange={(e) => setV((x) => ({ ...x, email: e.target.value }))}
          required
        />
        <TextField
          size="small"
          type="password"
          label="Password"
          value={v.password}
          onChange={(e) => setV((x) => ({ ...x, password: e.target.value }))}
          autoComplete="new-password"
          required
        />
        <TextField
          size="small"
          select
          label="Level"
          value={v.role}
          onChange={(e) => setV((x) => ({ ...x, role: e.target.value as StaffRole }))}
          sx={{ minWidth: 190 }}
        >
          {STAFF_ROLES.map((r) => (
            <MenuItem key={r} value={r}>
              {r}
            </MenuItem>
          ))}
        </TextField>
        <Button type="submit" variant="contained">
          Add
        </Button>
      </Paper>
    </Box>
  );
}
